import React from "react";
import { VideoThumb } from "./VideoExercicio.jsx";

// ─── Cores por tipo de bloco (adaptadas para light mode LesVita) ───────────
const BLOCK_TYPE_CONFIG = {
  warmup:       { label: "Aquecimento",   color: "#888780" },
  warm_up:      { label: "Aquecimento",   color: "#888780" },
  cooldown:     { label: "Cool-down",     color: "#888780" },
  custom:       { label: "Personalizado", color: "#888780" },
  workout:      { label: "Workout",       color: "#534AB7" },
  strength:     { label: "Força",         color: "#534AB7" },
  cardio:       { label: "Cardio",        color: "#0F6E56" },
  conditioning: { label: "Conditioning",  color: "#BA7517" },
  accessory:    { label: "Acessório",     color: "#993C1D" },
  core:         { label: "Core",          color: "#185FA5" },
};

const getBlockConfig = (type) =>
  BLOCK_TYPE_CONFIG[(type || "").toLowerCase()] ??
  { label: type || "Bloco", color: "#534AB7" };


// ─── Formata métricas funcionais (Hyrox/CrossFit) ───────────────────────────
const fmtDuracao = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 60) return `${n}s`;
  const min = Math.floor(n / 60);
  const sec = n % 60;
  return sec ? `${min}min${sec}s` : `${min}min`;
};

const fmtDistancia = (m) => {
  const n = Number(m);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n >= 1000 ? `${n / 1000}km` : `${n}m`;
};

// ─── Formata o valor de intensidade ─────────────────────────────────────────
const IntensityBadge = ({ system, value, color }) => {
  if (!value) return null;
  const prefix =
    system === "rpe"     ? "RPE " :
    system === "zone_lt" ? "Zona " :
    system === "percent" ? "" : "";
  const suffix =
    system === "percent" ? "%" : "";
  return (
    <span style={{
      fontWeight: 700,
      color,
      fontSize: 12,
    }}>
      {prefix}{value}{suffix}
    </span>
  );
};

// ─── Uma linha de exercício (standalone ou dentro de circuito) ───────────────
const ExerciseRow = ({ ex, color, onOpenVideo, getMedia, isCircuitChild = false }) => {
  const lib = ex.exercise_library ?? {};
  // Nome: payload da Lovable grava em `description`; fallback p/ lib.name e ex.nome.
  const nome = ex.description ?? lib.name ?? ex.nome ?? "Exercício";

  // Mídia via getMedia hoisteado (exercise_library do Supabase) com fallback p/ a
  // mídia inline do payload. Tolerante às grafias: video_url (Lovable) e video_url_1 (lib).
  const media = getMedia
    ? getMedia({
        video_url: ex.video_url ?? lib.video_url_1 ?? lib.video_url ?? null,
        video_url_2: ex.video_url_2 ?? lib.video_url_2 ?? null,
        thumbnail_url: ex.thumbnail_url ?? lib.thumbnail_url ?? null,
        EXERCISE_ID: ex.exercise_library_id ?? ex.EXERCISE_ID ?? lib.id ?? null,
        nome,
      })
    : { url: null, url2: null, thumbnail: null };
  const videoUrl = media.url;
  const videoUrl2 = media.url2;

  const metaItems = [
    ex.sets  ? `${ex.sets} séries` : null,
    ex.reps  ? `${ex.reps} reps`   : null,
    fmtDistancia(ex.distance_meters),
    fmtDuracao(ex.duration_seconds),
    ex.rest_interval ? `${ex.rest_interval} pausa` : null,
  ].filter(Boolean);

  return (
    <div style={{
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      paddingLeft: isCircuitChild ? 8 : 0,
      marginBottom: 8,
    }}>
      {/* Bullet */}
      <span style={{
        color,
        fontSize: isCircuitChild ? 11 : 13,
        lineHeight: "20px",
        flexShrink: 0,
        marginTop: 1,
      }}>
        {isCircuitChild ? "–" : "·"}
      </span>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 500,
          color: "var(--color-primary)",
          lineHeight: "18px",
        }}>
          {nome}
        </div>

        {metaItems.length > 0 && (
          <div style={{
            fontSize: 11,
            color: "var(--color-primary)",
            opacity: 0.55,
            marginTop: 1,
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            alignItems: "center",
          }}>
            {metaItems.map((m, i) => <span key={i}>{m}</span>)}
            {ex.intensity_value && (
              <>
                <span>·</span>
                <IntensityBadge
                  system={ex.intensity_system}
                  value={ex.intensity_value}
                  color={color}
                />
              </>
            )}
          </div>
        )}

        {ex.notes && (
          <p style={{
            fontSize: 11, color: "var(--color-primary)",
            opacity: 0.45, margin: "2px 0 0", lineHeight: 1.4,
          }}>
            {ex.notes}
          </p>
        )}
      </div>

      {/* Vídeo inline */}
      {videoUrl && (
        <VideoThumb
          url={videoUrl}
          thumbnail={media.thumbnail}
          width={72}
          height={52}
          onClick={() => onOpenVideo(videoUrl, nome, videoUrl2 || "")}
        />
      )}
    </div>
  );
};

// ─── Agrupa exercises em entries (standalone + circuits) ─────────────────────
// Exportado p/ reuso (ex.: personalRotinaAdapter achata blocos→exercícios) sem
// duplicar a regra. NÃO altera o comportamento de render do BlocoCard.
export const buildEntries = (block) => {
  // FIX 2 — Bloco vindo do Firestore (writer send-workout-routine) já traz a lista
  // COMPLETA e ordenada em `block.entries` (standalone + circuito + freetext). Ela
  // é SUPERSET de `block.exercises` (legado, só standalone) — então usá-la direto
  // cobre o bloco MISTO sem duplicar nem descartar. Só entra aqui quando há entries
  // pré-montadas; blocos 100% standalone (via block.exercises) e o formato cru do
  // Supabase (block_exercises/circuits, sem `entries`) seguem pelo caminho abaixo.
  const preBuilt = Array.isArray(block.entries) ? block.entries : [];
  if (preBuilt.length > 0) {
    return preBuilt
      .map((e, i) => {
        const order = Number(e.order ?? e.ordem ?? i);
        if (e.entry_type === "circuit") {
          return {
            type: "circuit",
            label: e.label ?? "Circuito",
            sets: e.sets,
            notes: e.notes ?? null, // FIX 3
            exercises: e.exercises ?? [],
            order,
          };
        }
        if (e.entry_type === "freetext") {
          // FIX 1 — o writer grava o texto em `content`/`freetext_content`, não em description/text.
          return {
            type: "freetext",
            text: e.freetext_content ?? e.content ?? e.description ?? e.text ?? "",
            order,
          };
        }
        return { type: "exercise", ex: e, order };
      })
      .sort((a, b) => a.order - b.order);
  }

  const exercises = block.block_exercises ?? block.exercises ?? [];
  const circuits  = block.block_exercise_circuits ?? block.circuits ?? [];

  // Mapeia circuit_id → circuito
  const circuitMap = {};
  circuits.forEach((c) => { circuitMap[c.id] = c; });

  const entries   = [];
  const seenCircuits = new Set();

  // Ordena por exercise_order
  const sorted = [...exercises].sort(
    (a, b) => (a.exercise_order ?? a.order ?? 0) - (b.exercise_order ?? b.order ?? 0)
  );

  sorted.forEach((ex) => {
    if (ex.entry_type === "freetext") {
      // FIX 1 — aceita freetext_content/content além de description.
      entries.push({ type: "freetext", text: ex.freetext_content ?? ex.content ?? ex.description ?? "", order: ex.exercise_order ?? 0 });
      return;
    }

    const cid = ex.circuit_id ?? null;
    if (cid && circuitMap[cid]) {
      if (!seenCircuits.has(cid)) {
        seenCircuits.add(cid);
        const circ = circuitMap[cid];
        const circExs = sorted.filter((e) => e.circuit_id === cid)
          .sort((a, b) => (a.circuit_order ?? 0) - (b.circuit_order ?? 0));
        entries.push({
          type: "circuit",
          label: circ.label ?? "Circuito",
          sets: circ.sets,
          notes: circ.notes ?? null, // FIX 3
          exercises: circExs,
          order: ex.exercise_order ?? 0,
        });
      }
    } else {
      entries.push({ type: "exercise", ex, order: ex.exercise_order ?? 0 });
    }
  });

  // Fallback (agora coberto pelo preBuilt no topo; mantido até validar em runtime).
  if (entries.length === 0 && (block.entries || []).length > 0) {
    return block.entries.map((e) => {
      if (e.entry_type === "circuit") return { type: "circuit", label: e.label, sets: e.sets, notes: e.notes ?? null, exercises: e.exercises ?? [], order: 0 };
      if (e.entry_type === "freetext") return { type: "freetext", text: e.freetext_content ?? e.content ?? e.description ?? e.text ?? "", order: 0 };
      return { type: "exercise", ex: e, order: 0 };
    });
  }

  return entries;
};

// ─── BlocoCard principal ────────────────────────────────────────────────────
const BlocoCard = ({ bloco, onOpenVideo, getMedia }) => {
  const { label, color } = getBlockConfig(bloco.block_type ?? bloco.tipo);
  const entries = buildEntries(bloco);

  const titulo    = bloco.title    ?? bloco.nome    ?? null;
  const structure = bloco.structure ?? bloco.estrutura ?? null;
  const objective = bloco.objective ?? bloco.objetivo ?? null;
  const sessionLabel = bloco.session_label ?? null;
  const isSecondary = !!sessionLabel;

  return (
    <div style={{
      borderLeft: `3px solid ${color}`,
      borderRadius: "0 12px 12px 0",
      background: isSecondary
        ? "rgba(0,0,0,0.03)"
        : "rgba(0,0,0,0.04)",
      padding: "10px 14px",
      marginBottom: 12,
      opacity: isSecondary ? 0.85 : 1,
    }}>
      {/* Badge tipo + Título */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8, }}>
        <span style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          padding: "2px 6px",
          borderRadius: 4,
          color,
          background: `${color}26`,
        }}>
          {label}
        </span>

        {titulo && (
          <span style={{ fontSize: 12, fontWeight: 500, color }}>
            {titulo}
          </span>
        )}
      </div>

      {/* Structure (AMRAP 20', EMOM, For Time...) — sublabel, renderiza mesmo sem title */}
      {structure && (
        <p style={{ fontSize: 11, fontWeight: 600, color, margin: "0 0 6px", letterSpacing: "0.02em" }}>
          {structure}
        </p>
      )}

      {/* Session label */}
      {sessionLabel && (
        <p style={{ fontSize: 11, color: "var(--color-primary)", opacity: 0.5, margin: "0 0 4px" }}>
          — {sessionLabel}
        </p>
      )}

      {/* Objetivo */}
      {objective && (
        <p style={{ fontSize: 11, color: "var(--color-primary)", opacity: 0.55, margin: "0 0 8px", lineHeight: 1.4 }}>
          {objective}
        </p>
      )}

      {/* Entries */}
      <div style={{ marginTop: 6 }}>
        {entries.map((entry, i) => {
          if (entry.type === "freetext") {
            return (
              <p key={i} style={{
                fontSize: 12,
                whiteSpace: "pre-line",
                color: "var(--color-primary)",
                opacity: 0.75,
                lineHeight: 1.6,
                margin: "0 0 6px",
              }}>
                {entry.text}
              </p>
            );
          }

          if (entry.type === "circuit") {
            return (
              <div key={i} style={{
                borderLeft: `2px solid ${color}40`,
                paddingLeft: 10,
                marginBottom: 10,
              }}>
                <div style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "var(--color-primary)",
                  opacity: 0.6,
                  marginBottom: 4,
                }}>
                  {entry.label}{entry.sets ? ` · ${entry.sets} séries` : ""}
                </div>
                {/* FIX 3 — notes do circuito (marcadores de execução do round) */}
                {entry.notes && (
                  <p style={{
                    fontSize: 11, color: "var(--color-primary)",
                    opacity: 0.5, margin: "0 0 4px", lineHeight: 1.4,
                  }}>
                    {entry.notes}
                  </p>
                )}
                {(entry.exercises || []).map((ex, j) => (
                  <ExerciseRow
                    key={j}
                    ex={ex}
                    color={color}
                    onOpenVideo={onOpenVideo}
                    getMedia={getMedia}
                    isCircuitChild
                  />
                ))}
              </div>
            );
          }

          // exercise
          return (
            <ExerciseRow
              key={i}
              ex={entry.ex}
              color={color}
              onOpenVideo={onOpenVideo}
              getMedia={getMedia}
            />
          );
        })}
      </div>
    </div>
  );
};

export default BlocoCard;